import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics } from '@bufferController/BufferMetrics/IBufferMetrics';
import { GetSideMetricsAsyncEvents } from '@bufferController/BufferMetrics/helpers/GetSideMetricsAsync.events';
import { EPlaybackRateMode } from '@bufferController/PlaybackSpeedController/IPlaybackSpeedController';
import { IPlaybackSpeedWatcherConfig } from '@bufferController/PlaybackSpeedController/PlaybackSpeedWatcher/PlaybackSpeedWatcher.config';
import { PlaybackSpeedEvents, TargetBufferEvents } from '@bufferController/PlaybackSpeedController/TargetBuffer.events';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Logger, TLogger } from '@logger/Logger';
import { StreamLoaderModuleEvents } from '@streamLoader/StreamLoader.events';
import { getPartHoldback } from '@utils/common/partHoldback';
import bind from '@utils/decorators/bind';
import { IHydratedManifest } from '@utils/m3u8Hydrator/m3u8Hydrator';

export class PlaybackSpeedWatcher extends ConfigConsumer<IPlaybackSpeedWatcherConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public groups: string[] = ['PlaybackSpeed'];

  private _originalTargetBuffer: number = 2;
  private _currentTargetBufferMultiplier: number = 1;
  private _currentPlaybackRateMode: EPlaybackRateMode = EPlaybackRateMode.NORMAL;
  private _isPaused: boolean = false;
  private _isDecriseable: boolean = true;
  private _currentOriginalTargetBufferMultiplier: number = 0;

  private readonly _onBufferMetricsReadyListener: string;
  private readonly _onTargetBufferIncreaseRequestedListener: string;
  private readonly _onTargetBufferDecreaseRequestedListener: string;
  private readonly _onPlaylistInfoAvailableListener: string;
  private readonly _onPlaybackSpeedControllerMetricsRequestedListener: string;
  private readonly _onOriginalTargetBufferDecreaseRequestedListener: string;
  private readonly _onOriginalTargetBufferDecreaseDisableListener: string;
  private readonly _logger: TLogger;

  constructor(eventBus: TEventBus) {
    super('playbackSpeedWatcher', eventBus);
    this._logger = Logger(eventBus, 'Doppio - PlaybackSpeedWatcher');

    this._onBufferMetricsReadyListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onBufferMetricsAvailable,
    );
    this._onTargetBufferDecreaseRequestedListener = this._eventBus.subscribe(
      TargetBufferEvents.targetBufferDecreaseRequested,
      this._targetBufferDecreaseRequested,
    );
    this._onTargetBufferIncreaseRequestedListener = this._eventBus.subscribe(
      TargetBufferEvents.targetBufferIncreaseRequested,
      this._targetBufferIncreaseRequested,
    );
    this._onPlaylistInfoAvailableListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlists.playlistInfo,
      this._onPlaylistInfoAvailable,
    );
    this._onPlaybackSpeedControllerMetricsRequestedListener = this._eventBus.subscribe(
      GetSideMetricsAsyncEvents.getPlaybackSpeedControllerMetrics,
      this._onPlaybackSpeedControllerMetricsRequested,
    );
    this._onOriginalTargetBufferDecreaseRequestedListener = this._eventBus.subscribe(
      TargetBufferEvents.originalTargetBufferDecreaseRequested,
      this._originalTargetBufferDecreaseRequested,
    );
    this._onOriginalTargetBufferDecreaseDisableListener = this._eventBus.subscribe(
      TargetBufferEvents.originalTargetBufferDisable,
      this._onOriginalTargetBufferDecreaseDisable,
    );

    if (this.config.targetBufferInitialMultiplier && this.config.targetBufferInitialMultiplier < 1) {
      this._logger.warn(
        'Target buffer multiplier set to value lower than 1. It is unsafe to load less than target-duration, forcing no multiplier instead',
      );
    }
  }

  @bind
  destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onBufferMetricsReadyListener);
    this._eventBus.unsubscribe(this._onTargetBufferDecreaseRequestedListener);
    this._eventBus.unsubscribe(this._onTargetBufferIncreaseRequestedListener);
    this._eventBus.unsubscribe(this._onPlaylistInfoAvailableListener);
    this._eventBus.unsubscribe(this._onPlaybackSpeedControllerMetricsRequestedListener);
    this._eventBus.unsubscribe(this._onOriginalTargetBufferDecreaseRequestedListener);
    this._eventBus.unsubscribe(this._onOriginalTargetBufferDecreaseDisableListener);
  }

  public pause(): void {
    this._isPaused = true;
    this._setNewPlaybackRateMode(EPlaybackRateMode.NORMAL);
  }

  public resume(): void {
    this._isPaused = false;
  }

  public _handleConfigUpdated(): void {}

  private _setNewPlaybackRateMode(playbackRateMode: EPlaybackRateMode): void {
    if (this._currentPlaybackRateMode === playbackRateMode) {
      return;
    }

    this._currentPlaybackRateMode = playbackRateMode;
    this._eventBus.publish(PlaybackSpeedEvents.setPlaybackSpeed, playbackRateMode);
  }

  @bind
  private _onBufferMetricsAvailable(metrics: IBufferMetrics): void {
    if (this._isPaused) {
      return;
    }

    const targetBufferAmount: number = this._getTargetBuffer();
    const distanceFromTarget: number = metrics.bufferAmountWithoutGaps - targetBufferAmount;

    if (metrics.currentPlaybackPosition === 0) {
      return;
    }

    if (distanceFromTarget > this.config.maxLiveSyncThreshold * targetBufferAmount) {
      this._setNewPlaybackRateMode(EPlaybackRateMode.FAST);
      return;
    }

    if (-1 * distanceFromTarget > this.config.minLiveSyncThreshold * targetBufferAmount) {
      this._setNewPlaybackRateMode(EPlaybackRateMode.SLOW);
      return;
    }

    if (this._currentPlaybackRateMode === EPlaybackRateMode.SLOW && distanceFromTarget > 0) {
      this._setNewPlaybackRateMode(EPlaybackRateMode.NORMAL);
      return;
    }

    if (this._currentPlaybackRateMode === EPlaybackRateMode.FAST && distanceFromTarget < 0) {
      this._setNewPlaybackRateMode(EPlaybackRateMode.NORMAL);
      return;
    }
  }

  @bind
  private _targetBufferIncreaseRequested(): void {
    if (this._currentTargetBufferMultiplier < this.config.maxTargetBufferMultiplier) {
      this._currentTargetBufferMultiplier += 1;
      this._eventBus.publish(TargetBufferEvents.targetBufferIncreased, {});
    }
  }

  @bind
  private _targetBufferDecreaseRequested(): void {
    if (this._currentTargetBufferMultiplier > 1) {
      this._currentTargetBufferMultiplier -= 1;
      this._eventBus.publish(TargetBufferEvents.targetBufferIncreased, {});
    }
  }

  @bind
  private _onPlaylistInfoAvailable(manifest: IHydratedManifest): void {
    this._originalTargetBuffer = getPartHoldback(manifest, manifest.targetDuration * 3);
  }

  @bind
  private _onPlaybackSpeedControllerMetricsRequested(): void {
    this._eventBus.publish(GetSideMetricsAsyncEvents.playbackSpeedControllerMetricsAvailable, this._getTargetBuffer());
  }

  private _getTargetBuffer(): number {
    return this._isDecriseable
      ? this._originalTargetBuffer - this._currentOriginalTargetBufferMultiplier * this.config.targetBufferDecreaseCoefficient
      : this._originalTargetBuffer * this._currentTargetBufferMultiplier * this.config.targetBufferInitialMultiplier;
  }

  get additionalInfo(): TDebuggableAdditionalInfo {
    return {
      currentTargetBufferMultiplier: this._currentTargetBufferMultiplier,
      currentTargetBuffer: this._getTargetBuffer(),
    };
  }
  @bind
  private _originalTargetBufferDecreaseRequested(): void {
    this._currentOriginalTargetBufferMultiplier += 1;
  }
  @bind
  private _onOriginalTargetBufferDecreaseDisable(): void {
    this._isDecriseable = false;
  }
}
